import React from 'react'
import { localeName, normalizeLocales } from './formBuilderModel'
import {
  DEFAULT_LOCALE,
  isFieldEditable,
  isReviewerOnlyField,
  isReviewerOnlySection,
  labelOf,
  optionLine,
  optionValue,
  pageTitle,
  shouldShowField,
  shouldShowSection,
} from './formFillAudience'

export function FormLocaleSwitch({ settings, locale, onChange, className = '' }) {
  const locales = normalizeLocales(settings)
  if (locales.length <= 1) return null
  return (
    <div className={`flex justify-end ${className}`.trim()}>
      <label className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs font-semibold text-slate-600">
        <span>Language</span>
        <select
          className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm font-medium text-slate-800"
          value={locale}
          onChange={(e) => onChange(e.target.value)}
          aria-label="Form language"
        >
          {locales.map((code) => (
            <option key={code} value={code}>{localeName(code)}</option>
          ))}
        </select>
      </label>
    </div>
  )
}

const inputClass =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-[15px] text-slate-900 outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-600/20'

export function fileAnswerItems(value) {
  if (value == null || value === '') return []
  const list = Array.isArray(value) ? value : [value]
  return list
    .map((item) => {
      if (item == null || item === '') return null
      if (typeof item === 'string') {
        if (item === 'file') return { name: null, legacy: true }
        return { name: item }
      }
      if (typeof item === 'object') {
        const name = item.name || item.filename || null
        if (!name && item.legacy !== true) return null
        return { name: name || null, size: item.size, mime: item.mime || item.type || null, legacy: !name }
      }
      return null
    })
    .filter(Boolean)
}

function formatFileSize(bytes) {
  const n = Number(bytes)
  if (!Number.isFinite(n) || n < 0) return ''
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

function FileAnswerList({ value }) {
  const items = fileAnswerItems(value)
  if (!items.length) return <p className="m-0 text-sm text-slate-500">No file uploaded</p>
  return (
    <ul className="m-0 space-y-1 p-0 list-none">
      {items.map((item, i) => (
        <li key={`${item.name || 'file'}-${i}`} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">
          {item.name || 'File selected — the name was not saved with this response'}
          {item.size != null && item.size !== '' ? (
            <span className="text-slate-500"> · {formatFileSize(item.size)}</span>
          ) : null}
        </li>
      ))}
    </ul>
  )
}

function conditionMet(cond, answers) {
  if (!cond || !cond.fieldId) return true
  const v = answers[cond.fieldId]
  const empty = v === undefined || v === '' || (Array.isArray(v) && v.length === 0)
  if (cond.operator === 'is_empty') return empty
  if (cond.operator === 'is_not_empty') return !empty
  if (empty) return false
  if (cond.operator === 'equals') return Array.isArray(v) ? v.includes(cond.value) : String(v) === String(cond.value)
  if (cond.operator === 'not_equals') return Array.isArray(v) ? !v.includes(cond.value) : String(v) !== String(cond.value)
  return true
}

export function flattenVisibleFields(definition, answers, fillRole = 'submitter') {
  const out = []
  for (const page of definition?.pages || []) {
    for (const section of page.sections || []) {
      if (!shouldShowSection(section, fillRole)) continue
      if (section.condition && !conditionMet(section.condition, answers)) continue
      for (const field of section.fields || []) {
        if (!shouldShowField(field, section, fillRole)) continue
        if (field.condition && !conditionMet(field.condition, answers)) continue
        out.push({ page, section, field })
      }
    }
  }
  return out
}

export default function FormFillFields({
  definition,
  answers,
  onChange,
  errors = {},
  readOnly = false,
  closed = false,
  fillRole = 'submitter',
  visibleSectionIds = null,
  locale = DEFAULT_LOCALE,
}) {
  const pages = definition?.pages || []
  return (
    <div className="space-y-8">
      {pages.map((page, pi) => {
        const sections = (page.sections || []).filter((s) => {
          if (!shouldShowSection(s, fillRole, visibleSectionIds)) return false
          return !s.condition || conditionMet(s.condition, answers)
        })
        if (!sections.length) return null
        const title = pageTitle(page, pi, locale)
        const pageDesc = labelOf(page.description, locale)
        return (
          <section key={page.id || pi}>
            {title ? <h2 className="text-lg font-semibold text-slate-900 mb-1">{title}</h2> : null}
            {pageDesc ? <p className="text-sm text-slate-500 mb-3">{pageDesc}</p> : null}
            {sections.map((section) => {
              const fields = (section.fields || []).filter((f) => {
                if (!shouldShowField(f, section, fillRole, visibleSectionIds)) return false
                return !f.condition || conditionMet(f.condition, answers)
              })
              if (!fields.length) return null
              const cols = section.columns === 3 ? 3 : section.columns === 2 ? 2 : 1
              const sectionLabel = labelOf(section.label, locale)
              const sectionDesc = labelOf(section.description, locale)
              const reviewerSection = isReviewerOnlySection(section)
              return (
                <div
                  key={section.id}
                  className={`mb-6 ${reviewerSection && fillRole === 'review' ? 'rounded-xl border border-violet-200 bg-violet-50/40 p-4' : ''}`}
                >
                  {sectionLabel || reviewerSection ? (
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      {sectionLabel ? <h3 className="text-base font-semibold text-slate-800 m-0">{sectionLabel}</h3> : null}
                      {reviewerSection ? (
                        <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[11px] font-semibold text-violet-800">Reviewer only</span>
                      ) : null}
                    </div>
                  ) : null}
                  {sectionDesc ? <p className="text-sm text-slate-500 mb-3">{sectionDesc}</p> : null}
                  <div className={`grid gap-4 ${cols === 3 ? 'sm:grid-cols-3' : cols === 2 ? 'sm:grid-cols-2' : 'grid-cols-1'}`}>
                    {fields.map((f) => (
                      <Field
                        key={f.id}
                        field={f}
                        section={section}
                        locale={locale}
                        value={answers[f.id]}
                        error={errors[f.id]}
                        readOnly={readOnly || !isFieldEditable(f, section, fillRole, closed)}
                        showReviewerBadge={fillRole === 'review' && isReviewerOnlyField(f, section) && !reviewerSection}
                        onChange={(v) => onChange(f.id, v)}
                      />
                    ))}
                  </div>
                </div>
              )
            })}
          </section>
        )
      })}
    </div>
  )
}

function Field({ field: f, section, value, onChange, error, readOnly, locale = DEFAULT_LOCALE, showReviewerBadge = false }) {
  const label = labelOf(f.label, locale) || f.id
  const placeholder = labelOf(f.placeholder, locale)
  const options = (f.options || []).filter((o) => String(optionValue(o) || '').trim() !== '')
  const full = f.span === 'full' || ['longtext', 'checkboxes'].includes(f.type)
  return (
    <div className={full ? 'sm:col-span-full' : ''}>
      <label className="block text-sm font-semibold text-slate-800 mb-1.5">
        {label}
        {f.required ? <span className="text-red-600 ml-0.5">*</span> : null}
        {showReviewerBadge ? (
          <span className="ml-2 rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold text-violet-800">Reviewer</span>
        ) : null}
      </label>
      {f.type === 'longtext' ? (
        <textarea className={inputClass} rows={4} value={value || ''} disabled={readOnly} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
      ) : f.type === 'dropdown' ? (
        <select className={inputClass} value={value || ''} disabled={readOnly || (f.catalog && !options.length)} onChange={(e) => onChange(e.target.value)}>
          <option value="">{f.catalog && !options.length ? 'Loading options…' : 'Select…'}</option>
          {options.map((o) => (
            <option key={optionValue(o)} value={optionValue(o)}>{optionLine(o, locale)}</option>
          ))}
        </select>
      ) : f.type === 'radio' || f.type === 'yesno' ? (
        <div className="flex flex-wrap gap-2">
          {(f.type === 'yesno' && !options.length ? ['Yes', 'No'] : options).map((o) => {
            const v = optionValue(o)
            const on = String(value) === String(v)
            return (
              <button
                key={v}
                type="button"
                disabled={readOnly}
                onClick={() => onChange(v)}
                className={`rounded-lg border px-3 py-2 text-sm font-medium ${on ? 'border-teal-600 bg-teal-50 text-teal-800' : 'border-slate-200 bg-white text-slate-700'}`}
              >
                {optionLine(o, locale)}
              </button>
            )
          })}
        </div>
      ) : f.type === 'checkboxes' ? (
        <div className="flex flex-col gap-2">
          {options.map((o) => {
            const v = optionValue(o)
            const selected = Array.isArray(value) && value.includes(v)
            return (
              <label key={v} className="flex items-center gap-2 text-sm text-slate-800">
                <input
                  type="checkbox"
                  disabled={readOnly}
                  checked={!!selected}
                  onChange={() => {
                    const cur = Array.isArray(value) ? value : []
                    onChange(selected ? cur.filter((x) => x !== v) : [...cur, v])
                  }}
                />
                {optionLine(o, locale)}
              </label>
            )
          })}
        </div>
      ) : f.type === 'file' ? (
        readOnly ? (
          <FileAnswerList value={value} />
        ) : (
          <div className="space-y-2">
            <input
              className="block w-full text-sm text-slate-700 file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm file:font-medium file:text-slate-700"
              type="file"
              multiple={!!f.multiple}
              accept={f.accept || undefined}
              onChange={(e) => {
                const picked = Array.from(e.target.files || []).map((file) => ({
                  name: file.name,
                  size: file.size,
                  mime: file.type || null,
                }))
                onChange(f.multiple ? picked : picked[0] || null)
              }}
            />
            {fileAnswerItems(value).length ? <FileAnswerList value={value} /> : null}
          </div>
        )
      ) : f.type === 'matrix' ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="text-left font-medium text-slate-500 py-1 pr-2" />
                {(options.length ? options : ['1', '2', '3']).map((o) => (
                  <th key={optionValue(o)} className="text-center font-medium text-slate-500 py-1 px-1">{optionLine(o, locale)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(f.rows || ['Row 1']).map((row, ri) => {
                const rowKey = optionValue(row) || optionLine(row, locale) || String(ri)
                const rowLabel = optionLine(row, locale) || rowKey
                return (
                <tr key={rowKey}>
                  <td className="py-1.5 pr-2 text-slate-800">{rowLabel}</td>
                  {(options.length ? options : ['1', '2', '3']).map((o) => {
                    const v = optionValue(o)
                    const on = (value || {})[rowKey] === v
                    return (
                      <td key={v} className="text-center">
                        <input type="radio" disabled={readOnly} checked={on} onChange={() => onChange({ ...(value || {}), [rowKey]: v })} />
                      </td>
                    )
                  })}
                </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <input
          className={inputClass}
          type={f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : f.type === 'email' ? 'email' : f.type === 'url' ? 'url' : 'text'}
          value={value || ''}
          disabled={readOnly}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
        />
      )}
      {error ? <div className="mt-1 text-sm font-semibold text-red-600">{error}</div> : null}
    </div>
  )
}
